export function arrayHasElements<T>(array: T[]) {
    return array.length > 0;
}

export function enforceArray<T>(arrayOrItem: T[] | T): T[] {
    return Array.isArray(arrayOrItem) ? arrayOrItem : [arrayOrItem];
}

/**
 * Removes an element from the provided array.
 *
 * @param inline - If `true` (default), removes the element from provided array,
 * mutating it and maintaining the reference. Otherwise, the array is left untouched
 * and a new array is returned.
 *
 * @returns The original array if `inline` is `true`; otherwise, a new array sans `removed`.
 */
export function removeArrayElement<T>(removed: T, array: T[], inline = true) {
    if (inline) {
        const index = array.indexOf(removed);
        if (index > -1) {
            array.splice(index, 1);
        }

        return array;
    }

    return array.filter(element => element !== removed);
}

/**
 * Produces a new array that contains the values only occurring in `target` but not in `source`.
 * Items that are in `source` but not `target` will not be considered (for that, use `symmetricDifference()`).
 * See [set theory definition](https://en.wikipedia.org/wiki/Complement_(set_theory)#Relative_complement).
 *
 * Prefer [`Set.prototype.difference()`](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set/difference)
 * when it becomes widely available.
 *
 * @param target The array to compare against `source`.
 * @param source The array to be compared against.
 */
export function difference<T>(target: T[], source: T[]) {
    return target.filter(value => !source.includes(value));
}

/**
 * Produces a new array that contains the values unique to each array.
 * See [set theory definition](https://en.wikipedia.org/wiki/Symmetric_difference).
 *
 * Prefer [`Set.prototype.symmetricDifference()`](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set/symmetricDifference)
 * when it becomes widely available.
 */
export function symmetricDifference<T>(a: T[], b: T[]) {
    const diffA = difference(a, b);
    const diffB = difference(b, a);
    // use a set to guarantee uniqueness
    return Array.from(new Set(diffA.concat(diffB))).sort();
}

/**
 * Create a new array of sub-arrays from a source array, never exceeding a maximum size.
 *
 * @param chunkSize The maximum number of elements each sub-array can contain.
 *
 * The function fills each sub-array to this size, with the last array
 * containing less elements if the total number of elements is
 * not evenly divisible by the chunk size.
 */
export function chunkArray<T>(arr: T[], chunkSize: number): T[][] {
    const { length } = arr;

    if (length === 0) {
        return [[]];
    }

    if (chunkSize < 1 || chunkSize > length) {
        throw new Error(`Invalid array chunk size. It must be between 1 and ${length}.`);
    }

    if (chunkSize === length) {
        return [[...arr]];
    }

    const chunks: Array<T[]> = [];
    for (let i = 0; i < length; i += chunkSize) {
        const chunk = arr.slice(i, i + chunkSize);
        chunks.push(chunk);
    }

    return chunks;
}

/**
 * De-duplicate an array of objects based on
 * an identity criteria other than object reference.
 */
export function dedupe<A = unknown>(items: A[], identity: keyof A) {
    const tuples = items.map(a => [a[identity], a] as const);

    // use a dict to de-dupe based on the specified identity key
    const map = new Map(tuples);

    return Array.from(map.values()).sort();
}
